import { FC, useCallback, useState } from "react";
import { StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from "react-native";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { ShimmerView } from "..";
import ComboBox, { tOption } from "../ComboBox";
import Arrow from "../impComponents/Arrow";
import {
  tCellData,
  tEmptyTableMessage,
  tHeaderItemProps,
  tHeaderProps,
  tRenderCellProps,
  tRenderRowProps,
  tTableData,
} from "./types";

export const sortColumn = (myRows: tTableData, myIndex?: number, myIsAscending?: boolean): tTableData => {
  if (typeof myIndex !== "number") return myRows;
  if (myIsAscending) {
    return [...myRows].sort((myRowA: tCellData[], myRowB: tCellData[]) => {
      let aFirstCell = myRowA[myIndex];
      let aSecondCell = myRowB[myIndex];
      if (
        typeof aFirstCell === "string" &&
        typeof aSecondCell === "string" &&
        aFirstCell.search(/^\$\d*.?\d*$/m) >= 0 &&
        aSecondCell.search(/^\$\d*.?\d*$/m) >= 0
      ) {
        aFirstCell = Number.parseFloat(aFirstCell.slice(1));
        aSecondCell = Number.parseFloat(aSecondCell.slice(1));
      }
      if (aFirstCell > aSecondCell) return 1;
      else if (aFirstCell < aSecondCell) return -1;
      return 0;
    });
  }
  return [...myRows].sort((myRowA: tCellData[], myRowB: tCellData[]) => {
    let aFirstCell = myRowA[myIndex];
    let aSecondCell = myRowB[myIndex];
    if (
      typeof aFirstCell === "string" &&
      typeof aSecondCell === "string" &&
      aFirstCell.search(/^\$\d*.?\d*$/m) >= 0 &&
      aSecondCell.search(/^\$\d*.?\d*$/m) >= 0
    ) {
      aFirstCell = Number.parseFloat(aFirstCell.slice(1));
      aSecondCell = Number.parseFloat(aSecondCell.slice(1));
    }
    if (aFirstCell < aSecondCell) return 1;
    else if (aFirstCell > aSecondCell) return -1;
    return 0;
  });
};

export const RenderRow: FC<tRenderRowProps> = ({
  item,
  index,
  loading,
  onRowPress,
  shadeAlternates,
  headerData,
  RenderCell,
  _handleOnRowPress,
  badgeColumnConfigs,
}) => {
  const _handleOnPress = useCallback(
    (myCellIndex: number) => {
      _handleOnRowPress(item, index, myCellIndex);
    },
    [_handleOnRowPress, index, item],
  );
  return (
    <ShimmerView loading={loading}>
      {onRowPress ? (
        <View style={[styles.tableRow, !!shadeAlternates && index % 2 === 1 ? styles.tableRowShaded : null]}>
          {item.slice(0, headerData.length).map((myTableCell, myCellIndex: number) => (
            <TouchableOpacity
              key={index.toString()}
              style={styles.gTableCell}
              onPress={() => _handleOnPress(myCellIndex)}
            >
              <RenderCell
                myCellData={myTableCell}
                myCellIndex={myCellIndex}
                badgeColumnConfigs={badgeColumnConfigs}
                key={`${headerData[myCellIndex]}: ${myTableCell.toString()}`}
              />
            </TouchableOpacity>
          ))}
        </View>
      ) : (
        <View style={[styles.tableRow, !!shadeAlternates && index % 2 === 1 ? styles.tableRowShaded : null]}>
          {item.map((myTableCell, myCellIndex) => (
            <RenderCell
              myCellData={myTableCell}
              myCellIndex={myCellIndex}
              badgeColumnConfigs={badgeColumnConfigs}
              key={`${headerData[myCellIndex]}: ${myTableCell.toString()}`}
            />
          ))}
        </View>
      )}
    </ShimmerView>
  );
};

export const RenderCard: FC<tRenderRowProps> = ({
  item,
  index,
  loading,
  _handleOnRowPress,
  headerData,
  onRowPress,
  badgeColumnConfigs,
}) => {
  const _handleOnPress = useCallback(
    (myCellIndex: number) => {
      _handleOnRowPress(item, index, myCellIndex);
    },
    [_handleOnRowPress, index, item],
  );

  if (onRowPress) {
    return (
      <ShimmerView loading={loading}>
        <View style={styles.tableCard}>
          {item.map((myCell, myIndex) => {
            return (
              <View key={`${headerData[myIndex]}: ${myCell.toString()}`} style={styles.tableCardCellWithLabel}>
                <TouchableOpacity onPress={() => _handleOnPress(myIndex)}>
                  <Text style={styles.tableCardCellLabel}>{headerData[myIndex]}</Text>
                  <RenderCell
                    myCellData={myCell}
                    myCellIndex={myIndex}
                    isCard={true}
                    badgeColumnConfigs={badgeColumnConfigs}
                  />
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      </ShimmerView>
    );
  }
  return (
    <View style={styles.tableCard}>
      {item.map((myCell, myIndex) => (
        <View key={`${headerData[myIndex]}: ${myCell.toString()}`} style={styles.tableCardCellWithLabel}>
          <Text style={styles.tableCardCellLabel}>{headerData[myIndex]}</Text>
          <RenderCell myCellData={myCell} myCellIndex={myIndex} isCard={true} badgeColumnConfigs={badgeColumnConfigs} />
        </View>
      ))}
    </View>
  );
};

export const RenderCell: FC<tRenderCellProps> = ({ myCellData, myCellIndex, isCard, badgeColumnConfigs }) => {
  const aBadgeColumnConfig = badgeColumnConfigs?.find(
    (myBadgeColumnConfig) => myBadgeColumnConfig.columnIndex === myCellIndex,
  );
  if (aBadgeColumnConfig) {
    let aDotStyle: StyleProp<ViewStyle> = {};
    if (myCellData === aBadgeColumnConfig.redValue) {
      aDotStyle = {
        backgroundColor: kColors.secondaryRed,
      };
    } else if (myCellData === aBadgeColumnConfig.orangeValue) {
      aDotStyle = {
        backgroundColor: kColors.secondaryOrange,
      };
    } else if (myCellData === aBadgeColumnConfig.yellowValue) {
      aDotStyle = {
        backgroundColor: kColors.secondaryYellow,
      };
    } else if (myCellData === aBadgeColumnConfig.greenValue) {
      aDotStyle = {
        backgroundColor: kColors.secondaryGreen,
      };
    }
    return (
      <View key={myCellData.toString() + myCellIndex}>
        <View style={styles.gTableBadgeContainer}>
          <View style={[styles.gTableDotView, aDotStyle]} />
          <Text style={styles.gTableCellText}>{myCellData}</Text>
        </View>
      </View>
    );
  }
  return (
    <View key={myCellData.toString() + myCellIndex} style={isCard && styles.tableCardCell}>
      <Text style={styles.gTableCellText}>{myCellData}</Text>
    </View>
  );
};

export const EmptyTableMessage: FC<tEmptyTableMessage> = ({ myMessage }) => (
  <View style={styles.gTableEmptyMessageView}>
    <Text style={styles.gTableEmptyMessageText}>{myMessage}</Text>
  </View>
);

const HeaderBigItem: FC<tHeaderItemProps> = ({ myHeader, myIndex, currentSortConfig, _handleColumnSort }) => {
  const _handleOnPress = useCallback(() => _handleColumnSort({ myIndex }), [_handleColumnSort, myIndex]);

  return (
    <View key={myHeader} style={styles.gTableCell}>
      <TouchableOpacity style={styles.tableHeaderButton} onPress={_handleOnPress}>
        <Text
          style={
            currentSortConfig?.columnIndex === myIndex ? styles.tableHeaderLabelSortActive : styles.tableHeaderLabel
          }
          numberOfLines={1}
          ellipsizeMode={"tail"}
        >
          {myHeader}
        </Text>
        <View style={styles.gTableSortArrow}>
          <Arrow
            weight={currentSortConfig?.columnIndex === myIndex ? "bold" : "normal"}
            direction={currentSortConfig?.columnIndex === myIndex && currentSortConfig?.ascending ? "up" : "down"}
          />
        </View>
      </TouchableOpacity>
    </View>
  );
};

export const HeaderBig: FC<tHeaderProps> = ({ headerData, _handleColumnSort, currentSortConfig }) => {
  return (
    <View style={styles.tableHeader}>
      {headerData.map((myHeader, myIndex) => (
        <HeaderBigItem
          myHeader={myHeader}
          myIndex={myIndex}
          currentSortConfig={currentSortConfig}
          _handleColumnSort={_handleColumnSort}
          key={myHeader}
        />
      ))}
    </View>
  );
};

export const HeaderSmall: FC<tHeaderProps> = ({ headerData, _handleColumnSort }) => {
  const [sSortIndex, _setSortIndex] = useState<number>(-1);
  const [sIsAscending, _setIsAscending] = useState<boolean>(true);

  const kSortOptions: Array<tOption> = headerData.map((myHeaderItem) => ({
    myValue: myHeaderItem,
  }));
  const kOrderOptions: Array<tOption<"Ascending" | "Descending">> = [
    {
      myValue: "Ascending",
    },
    {
      myValue: "Descending",
    },
  ];

  const _handleOnSort = useCallback(
    (_, myIndex) => {
      _setSortIndex(myIndex);
      _handleColumnSort({ myIndex: myIndex, isAscending: sIsAscending });
    },
    [_handleColumnSort, sIsAscending],
  );

  const _handleOnOrder = useCallback(
    (myOption, _) => {
      _setIsAscending(myOption.myValue === "Ascending");
      _handleColumnSort({ myIndex: sSortIndex, isAscending: myOption.myValue === "Ascending" });
    },
    [_handleColumnSort, sSortIndex],
  );

  return (
    <>
      <View style={styles.gTableMobileSortOptionView1}>
        <Text style={styles.gTableMobileSortLabels}>Sort by...</Text>
        <ComboBox myOptions={kSortOptions} myPlaceholder="Sort by..." onSelect={_handleOnSort} />
      </View>
      <View style={styles.gTableMobileSortOptionView2}>
        <Text style={styles.gTableMobileSortLabels}>Order by...</Text>
        <ComboBox
          myOptions={kOrderOptions}
          myPlaceholder="Order by..."
          onSelect={_handleOnOrder}
          myDefaultIndex={0}
          disabled={sSortIndex === -1}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  gTableBadgeContainer: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
  },
  gTableCell: {
    alignItems: "flex-start",
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
    overflow: "hidden",
    paddingHorizontal: kStyleConst.kSpacing * 2,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  gTableCellText: {
    ...kFonts.body,
    flex: 1,
  },
  gTableDotView: {
    backgroundColor: kColors.brandDarkTransparent,
    borderRadius: kStyleConst.kTableDotSizing / 2,
    height: kStyleConst.kTableDotSizing,
    marginEnd: kStyleConst.kSpacing,
    width: kStyleConst.kTableDotSizing,
  },
  gTableEmptyMessageText: {
    ...kFonts.bodyItalic,
    color: kColors.mediumGrey,
  },
  gTableEmptyMessageView: {
    marginVertical: kStyleConst.kSpacing * 5,
    textAlign: "center",
  },
  gTableMobileSortLabels: {
    ...kFonts.formLegend,
    marginVertical: kStyleConst.kSpacing,
  },
  gTableMobileSortOptionView1: {
    flexDirection: "column",
    marginTop: kStyleConst.kSpacing * 2,
    zIndex: 15,
  },
  gTableMobileSortOptionView2: {
    flexDirection: "column",
    marginTop: kStyleConst.kSpacing * 2,
    zIndex: 10,
  },
  gTableSortArrow: {
    ...kStyleConst.kTableSortArrowSizing,
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  tableCard: {
    flexDirection: "row",
    margin: 16,
    paddingHorizontal: 20,
    paddingTop: 16,
    shadowColor: kColors.lightBlack,
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 16,
  },
  tableCardCell: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  tableCardCellLabel: {
    color: kColors.gray,
    overflow: "visible",
  },
  tableCardCellWithLabel: {
    alignItems: "stretch",
    flex: 1,
    marginBottom: kStyleConst.kSpacing * 3,
    overflow: "hidden",
  },
  tableHeader: {
    backgroundColor: kColors.white,
    borderBottomColor: kColors.brandPink,
    borderBottomWidth: 2,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  tableHeaderButton: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  tableHeaderLabel: {
    ...kFonts.body,
  },
  tableHeaderLabelSortActive: {
    ...kFonts.body,
    fontWeight: "600",
  },
  tableRow: {
    alignItems: "center",
    backgroundColor: kColors.white,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  tableRowShaded: {
    backgroundColor: kColors.almostWhite,
  },
});
